import { useState, useEffect } from 'react';
import { Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, Smartphone, Camera, TrendingUp, AlertCircle, Activity, Eye, MapPin } from 'lucide-react';

const ModernCafeDashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [peopleCount, setPeopleCount] = useState(47);
  const [mobileUsage, setMobileUsage] = useState(68);
  const [avgDwellTime, setAvgDwellTime] = useState(28);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      setPeopleCount(prev => Math.max(25, Math.min(85, prev + Math.floor(Math.random() * 7) - 3)));
      setMobileUsage(prev => Math.max(45, Math.min(95, prev + Math.floor(Math.random() * 11) - 5)));
      setAvgDwellTime(prev => Math.max(15, Math.min(45, prev + Math.floor(Math.random() * 5) - 2)));
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const peopleData = [
    { time: '8:00', count: 18, capacity: 90 },
    { time: '9:00', count: 32, capacity: 90 },
    { time: '10:00', count: 48, capacity: 90 },
    { time: '11:00', count: 61, capacity: 90 },
    { time: '12:00', count: 78, capacity: 90 },
    { time: '1:00', count: 82, capacity: 90 },
    { time: '2:00', count: 65, capacity: 90 },
    { time: '3:00', count: 52, capacity: 90 },
    { time: '4:00', count: 43, capacity: 90 },
    { time: '5:00', count: 38, capacity: 90 },
  ];

  const mobileData = [
    { hour: '8AM', usage: 52 },
    { hour: '9AM', usage: 58 },
    { hour: '10AM', usage: 71 },
    { hour: '11AM', usage: 78 },
    { hour: '12PM', usage: 85 },
    { hour: '1PM', usage: 81 },
    { hour: '2PM', usage: 69 },
    { hour: '3PM', usage: 63 },
  ];

  const zoneDistribution = [
    { name: 'Seating Area', value: 42, color: '#6366f1' },
    { name: 'Counter Queue', value: 28, color: '#8b5cf6' },
    { name: 'Entrance', value: 18, color: '#ec4899' },
    { name: 'Outdoor', value: 12, color: '#f59e0b' },
  ];

  const dwellTimeData = [
    { zone: 'Counter', time: 5.2 },
    { zone: 'Seating', time: 32.5 },
    { zone: 'Entrance', time: 2.8 },
    { zone: 'Outdoor', time: 28.3 },
    { zone: 'Restroom', time: 3.5 },
  ];

  const cameraStatus = [
    { id: 'CAM-01', status: 'online', location: 'Main Entrance', fps: 30, lastSeen: 'Just now' },
    { id: 'CAM-02', status: 'online', location: 'Seating Area North', fps: 30, lastSeen: 'Just now' },
    { id: 'CAM-03', status: 'online', location: 'Service Counter', fps: 30, lastSeen: 'Just now' },
    { id: 'CAM-04', status: 'online', location: 'Seating Area South', fps: 30, lastSeen: 'Just now' },
    { id: 'CAM-05', status: 'online', location: 'Outdoor Patio', fps: 30, lastSeen: 'Just now' },
  ];

  const recentAlerts = [
    { id: 1, type: 'Over crowding', camera: 'CAM-02', time: '13 mins ago', severity: 'warning', message: 'Seating area approaching 85% capacity' },
    { id: 2, type: 'Mobile Usage', camera: 'CAM-03', time: '24 mins ago', severity: 'info', message: 'Mobile usage above 80% at counter area' },
    { id: 3, type: 'Queue Alert', camera: 'CAM-03', time: '1 hour ago', severity: 'warning', message: 'Counter queue exceeded 5 people' },
  ];

  const MetricCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className={`p-2.5 rounded-xl bg-gradient-to-br ${color}`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-1">{value}</h3>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
        {trend && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
            trend > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
          }`}>
            <TrendingUp className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
            {Math.abs(trend)}%
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                Vision AI Analytics
              </h1>
              <p className="text-gray-600 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                {currentTime.toLocaleString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100">
                <p className="text-xs text-gray-500 mb-0.5">System Status</p>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                  <span className="text-sm font-semibold text-gray-900">All Systems Online</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <MetricCard
            title="Live Occupancy"
            value={peopleCount}
            subtitle="Current visitors in venue"
            icon={Users}
            color="from-blue-500 to-blue-600"
            trend={12}
          />
          <MetricCard
            title="Mobile Usage"
            value={`${mobileUsage}%`}
            subtitle="Customers on devices"
            icon={Smartphone}
            color="from-purple-500 to-purple-600"
            trend={-5}
          />
          <MetricCard
            title="Avg Dwell Time"
            value={`${avgDwellTime}m`}
            subtitle="Average time spent"
            icon={Activity}
            color="from-emerald-500 to-emerald-600"
            trend={8}
          />
          <MetricCard
            title="Active Cameras"
            value="5/5"
            subtitle="All systems operational"
            icon={Camera}
            color="from-rose-500 to-rose-600"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Occupancy Trends
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={peopleData}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="time" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }} 
                />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#6366f1" 
                  strokeWidth={3}
                  fill="url(#colorCount)" 
                  name="Visitors"
                />
                <Line 
                  type="monotone" 
                  dataKey="capacity" 
                  stroke="#94a3b8" 
                  strokeDasharray="5 5" 
                  strokeWidth={2}
                  name="Capacity"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-600" />
              Zone Distribution
            </h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={zoneDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {zoneDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '12px'
                  }} 
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              {zoneDistribution.map((zone, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: zone.color }}></div>
                    <span className="text-gray-700">{zone.name}</span>
                  </div>
                  <span className="font-semibold text-gray-900">{zone.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Mobile Usage Chart */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-purple-600" />
              Mobile Device Engagement
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={mobileData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="hour" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '12px'
                  }} 
                />
                <Bar dataKey="usage" fill="#8b5cf6" radius={[8, 8, 0, 0]} name="Usage %" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Dwell Time Chart */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-600" />
              Average Dwell Time by Zone
            </h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={dwellTimeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <YAxis dataKey="zone" type="category" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '12px'
                  }} 
                />
                <Bar dataKey="time" fill="#10b981" radius={[0, 8, 8, 0]} name="Minutes" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Camera Status and Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Camera Status */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Camera Network Status
            </h3>
            <div className="space-y-3">
              {cameraStatus.map((camera) => (
                <div key={camera.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      camera.status === 'online' ? 'bg-emerald-100' : 'bg-red-100'
                    }`}>
                      <Eye className={`w-5 h-5 ${
                        camera.status === 'online' ? 'text-emerald-600' : 'text-red-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{camera.id}</p>
                      <p className="text-sm text-gray-600">{camera.location}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 justify-end mb-1">
                      <div className={`w-2 h-2 rounded-full ${
                        camera.status === 'online' ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'
                      }`}></div>
                      <span className={`text-sm font-medium ${
                        camera.status === 'online' ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        {camera.status === 'online' ? 'Online' : 'Offline'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{camera.fps} FPS • {camera.lastSeen}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Alerts */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              Recent Insights & Alerts
            </h3>
            <div className="space-y-3">
              {recentAlerts.map((alert) => (
                <div key={alert.id} className="p-4 bg-gray-50 rounded-xl border-l-4 hover:bg-gray-100 transition-colors" style={{
                  borderLeftColor: alert.severity === 'warning' ? '#f59e0b' : alert.severity === 'error' ? '#ef4444' : '#3b82f6'
                }}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${
                        alert.severity === 'warning' ? 'text-orange-700' : 
                        alert.severity === 'error' ? 'text-red-700' : 'text-blue-700'
                      }`}>
                        {alert.type}
                      </span>
                      <span className="text-xs text-gray-500">• {alert.camera}</span>
                    </div>
                    <span className="text-xs text-gray-500">{alert.time}</span>
                  </div>
                  <p className="text-sm text-gray-700">{alert.message}</p>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300">
              View All Insights
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernCafeDashboard;